/**
 * Le Desk — RestrictionDialog (spec rev122, 2026-02).
 *
 * Modale de configuration d'une restriction d'accès pour un type de
 * document OU une catégorie. Composant réutilisable — reçoit ``kind``
 * (``"type"`` | ``"category"``) et ``id`` (type_id / category_id).
 *
 * Comportement :
 *   • Interrupteur « Restriction active » (bool).
 *   • Liste des utilisateurs autorisés + 4 checkboxes chacun :
 *     Voir / Créer / Modifier / Supprimer.
 *   • Premier Admin & admins : accès total non modifiable (visible en
 *     lecture seule avec badge « Accès total »).
 *   • « + Ajouter un utilisateur » : combobox parmi les non-admins non
 *     encore listés.
 *   • Enregistre via ``PUT /api/user-type-permissions/restriction/{kind}/{id}``.
 */
import { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ShieldCheck, X, Search, ChevronUp } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { AvatarBlock } from "@/components/settings/AccountSection";

const ACTIONS = [
  { key: "view",    label: "Voir" },
  { key: "create",  label: "Créer" },
  { key: "edit",    label: "Modifier" },
  { key: "advance", label: "Supprimer" },  // "advance" = 4e action = suppression dans la spec métier
];

export default function RestrictionDialog({
  open, onOpenChange,
  kind,               // "type" | "category"
  id,                 // type_id ou category_id
  label,              // libellé humain (ex. "Chèque reçu")
  users,              // [{id, first_name, last_name, email, role, is_first_admin}]
  onSaved,            // callback (config) → parent refresh
}) {
  const [active, setActive] = useState(false);
  const [entries, setEntries] = useState([]);   // [{user_id, actions[]}]
  // Spec rev143 (2026-02-06) — Admins exclus au cas par cas (par le Premier
  // Admin uniquement) de CETTE restriction précise. Le Premier Admin ne
  // peut jamais figurer dans cette liste (garde-fou backend).
  const [adminExcludedIds, setAdminExcludedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  // Règle plateforme : les droits par type (par utilisateur) se règlent dans la fenêtre « Droits d'accès ».
  // Ici, pour un type : liste en lecture + lien vers la fenêtre de chaque utilisateur.
  const readOnlyUsers = kind === "type";
  const navigate = useNavigate();

  const toggleAdminExcluded = (uid, isFirst) => {
    if (isFirst) return;  // garde-fou : jamais le Premier Admin.
    setAdminExcludedIds((prev) => (
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    ));
  };

  // Load config quand le dialog s'ouvre.
  useEffect(() => {
    if (!open || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/user-type-permissions/restriction/${kind}/${id}`);
        if (cancelled) return;
        setActive(!!data.active);
        setAdminExcludedIds(Array.isArray(data.admin_excluded_ids) ? data.admin_excluded_ids : []);
        // Spec rev138 (2026-02-06) — normalise `actions` : les données
        // legacy en base sont parfois stockées comme dict
        // `{view:true, create:false, modify:false, suppress:false}`.
        // On convertit en tableau de clés canoniques `view/create/edit/advance`.
        const KEY_MAP = { modify: "edit", suppress: "advance" };
        const normalized = (data.entries || []).map((e) => {
          let acts = e.actions;
          if (Array.isArray(acts)) {
            return { user_id: e.user_id, actions: acts };
          }
          if (acts && typeof acts === "object") {
            const list = [];
            for (const [k, v] of Object.entries(acts)) {
              if (!v) continue;
              list.push(KEY_MAP[k] || k);
            }
            return { user_id: e.user_id, actions: list };
          }
          return { user_id: e.user_id, actions: [] };
        });
        setEntries(normalized);
      } catch (e) {
        toast.error("Impossible de charger la configuration");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, kind, id]);

  const admins = (users || []).filter((u) => u.is_first_admin || u.role === "admin");
  const nonAdmins = (users || []).filter((u) => !u.is_first_admin && u.role !== "admin");
  const usedIds = new Set(entries.map((e) => e.user_id));
  const available = nonAdmins.filter((u) => !usedIds.has(u.id));

  const toggleAction = (userId, action) => {
    setEntries((prev) => prev.map((e) => {
      if (e.user_id !== userId) return e;
      const has = (e.actions || []).includes(action);
      const next = has
        ? (e.actions || []).filter((a) => a !== action)
        : [...(e.actions || []), action];
      return { ...e, actions: next };
    }));
  };

  const removeUser = (userId) => {
    setEntries((prev) => prev.filter((e) => e.user_id !== userId));
  };

  const addUser = (userId) => {
    setEntries((prev) => [...prev, { user_id: userId, actions: ["view"] }]);
    setAddSearch("");
    // On garde le panneau ouvert pour ajout multiple rapide.
  };

  const filteredAvailable = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    if (!q) return available;
    return available.filter((u) => {
      const nm = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
      return nm.includes(q);
    });
  }, [available, addSearch]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        active,
        entries: entries
          .filter((e) => (e.actions || []).length > 0)
          .map((e) => ({ user_id: e.user_id, actions: e.actions })),
        // Spec rev143 (2026-02-06) — persistance des exclusions d'admin.
        admin_excluded_ids: adminExcludedIds,
      };
      const { data } = await api.put(`/user-type-permissions/restriction/${kind}/${id}`, payload);
      const excludedN = (data.admin_excluded_ids || []).length;
      toast.success(
        data.active
          ? `Restriction active · ${data.entries.length} utilisateur${data.entries.length > 1 ? "s" : ""} autorisé${data.entries.length > 1 ? "s" : ""}${excludedN > 0 ? ` · ${excludedN} admin${excludedN > 1 ? "s" : ""} exclu${excludedN > 1 ? "s" : ""}` : ""}`
          : "Restriction désactivée"
      );
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const kindLabel = kind === "type" ? "type" : "catégorie";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px] max-h-[80vh] overflow-y-auto"
        data-testid="restriction-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} style={{ color: "#F97316" }} />
            Restriction d'accès — {label}
          </DialogTitle>
        </DialogHeader>

        {/* Toggle actif */}
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-md mb-3"
          style={{
            background: active ? "rgba(249,115,22,0.10)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${active ? "rgba(249,115,22,0.35)" : "rgba(255,255,255,0.10)"}`,
          }}
        >
          <Switch
            checked={active}
            onCheckedChange={setActive}
            data-testid="restriction-toggle"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium" style={{ color: active ? "#F97316" : "#E5E5E5" }}>
              Restriction {active ? "active" : "désactivée"}
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "#8B8F94" }}>
              {active
                ? `Seuls les utilisateurs listés ci-dessous auront accès à ce ${kindLabel} et à ses documents. Les autres ne le verront nulle part.`
                : `Toutes les autorisations reposent sur la matrice de rôles globale. Aucun utilisateur n'est bloqué explicitement.`}
            </div>
          </div>
        </div>

        {/* Premier Admin & admins — spec rev143 : le Premier Admin
            peut désormais EXCLURE un admin précis de cette restriction
            (le Premier Admin reste toujours protégé). */}
        {admins.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#8B8F94" }}>
              Accès total (réglable par premier admin)
            </div>
            <div className="space-y-1">
              {admins.map((u) => {
                const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
                const isFirst = !!u.is_first_admin;
                const isExcluded = adminExcludedIds.includes(u.id);
                return (
                  <div
                    key={u.id}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded"
                    style={{
                      background: isExcluded
                        ? "rgba(239,68,68,0.08)"
                        : "rgba(139,92,246,0.08)",
                      border: `1px solid ${isExcluded ? "rgba(239,68,68,0.35)" : "rgba(139,92,246,0.25)"}`,
                    }}
                    data-testid={`restriction-admin-row-${u.id}`}
                  >
                    <AvatarBlock user={u} size={24} />
                    <div className="flex-1 min-w-0 text-[12px]">
                      <span
                        className="truncate"
                        style={{
                          textDecoration: isExcluded ? "line-through" : "none",
                          color: isExcluded ? "#9CA3AF" : "inherit",
                        }}
                      >
                        {name}
                      </span>
                      {isExcluded && (
                        <div className="text-[10.5px] italic mt-0.5" style={{ color: "#FCA5A5" }}>
                          Perd l'accès à cette {kindLabel} uniquement — garde son rôle admin partout ailleurs.
                        </div>
                      )}
                    </div>
                    <span
                      className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
                      style={
                        isFirst
                          ? { background: "rgba(139,92,246,0.20)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.35)" }
                          : isExcluded
                            ? { background: "rgba(239,68,68,0.20)", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.45)" }
                            : { background: "rgba(139,92,246,0.20)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.35)" }
                      }
                    >
                      {isFirst ? "Premier admin" : (isExcluded ? "Exclu" : "Admin")}
                    </span>
                    {!isFirst && (
                      <button
                        type="button"
                        onClick={() => toggleAdminExcluded(u.id, isFirst)}
                        className="text-[10.5px] px-2 py-1 rounded hover:brightness-125 transition"
                        style={
                          isExcluded
                            ? { background: "rgba(74,222,128,0.10)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.35)" }
                            : { background: "rgba(255,255,255,0.03)", color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.10)" }
                        }
                        data-testid={`restriction-admin-toggle-${u.id}`}
                        title={
                          isExcluded
                            ? `Réintégrer ${name} dans cette restriction`
                            : `Exclure ${name} de cette restriction`
                        }
                      >
                        {isExcluded ? "Réintégrer" : "Exclure"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Utilisateurs listés */}
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#8B8F94" }}>
            Utilisateurs autorisés
          </div>
          {loading ? (
            <div className="text-[12px] text-muted-foreground italic py-2">Chargement…</div>
          ) : entries.length === 0 ? (
            <div className="text-[12px] italic py-2" style={{ color: "#8B8F94" }}>
              {active
                ? "Aucun utilisateur autorisé — seuls les admins auront accès."
                : readOnlyUsers ? "Aucun utilisateur n'a de droit sur ce type." : "Aucun utilisateur listé — activez la restriction pour commencer."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {entries.map((e) => {
                const u = (users || []).find((x) => x.id === e.user_id);
                if (!u) return null;
                const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
                return (
                  <div
                    key={e.user_id}
                    className="flex items-center gap-2 px-2.5 py-2 rounded"
                    style={{ background: "#1A1A1A", border: "1px solid #2A2A2A" }}
                    data-testid={`restriction-entry-${e.user_id}`}
                  >
                    <AvatarBlock user={u} size={24} />
                    <div className="flex-1 min-w-0 text-[12px] truncate">{name}</div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {readOnlyUsers && (
                        <button type="button" onClick={() => { onOpenChange(false); navigate(`/parametres?tab=users&rights=${e.user_id}`); }}
                          className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded hover:bg-white/[0.06]" style={{ color: "#7fb4ec", border: "0.5px solid #3568B0" }}
                          data-testid={`restriction-open-rights-${e.user_id}`}><ShieldCheck size={11} /> Droits d'accès</button>
                      )}
                      {ACTIONS.map((a) => {
                        const on = (e.actions || []).includes(a.key);
                        if (readOnlyUsers && !on) return null;
                        return (
                          <button
                            key={a.key}
                            type="button"
                            disabled={readOnlyUsers}
                            onClick={() => toggleAction(e.user_id, a.key)}
                            className="text-[10px] font-medium px-2 py-1 rounded transition-colors"
                            style={{
                              background: on ? "#2B579A" : "rgba(255,255,255,0.05)",
                              color: on ? "#FFFFFF" : "#8B8F94",
                              border: on ? "1px solid #2B579A" : "1px solid rgba(255,255,255,0.10)",
                            }}
                            data-testid={`restriction-action-${e.user_id}-${a.key}`}
                            title={a.label}
                          >
                            {a.label}
                          </button>
                        );
                      })}
                      {!readOnlyUsers && <button
                        type="button"
                        onClick={() => removeUser(e.user_id)}
                        className="p-1 rounded hover:bg-white/10 text-red-400 hover:text-red-300"
                        data-testid={`restriction-remove-${e.user_id}`}
                        title="Retirer cet utilisateur"
                      >
                        <Trash2 size={12} />
                      </button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* + Ajouter — spec rev141 (2026-02-06) : la section est
              désormais TOUJOURS rendue (fini le guard `available.length > 0`
              qui masquait tout le CTA quand tous les utilisateurs
              étaient soit admins soit déjà listés — le user pensait le
              composant absent). Un message explicite explique le cas
              vide, et le CTA se désactive quand il n'y a rien à ajouter. */}
          {readOnlyUsers && (
            <div className="mt-2 text-[11px]" style={{ color: "#8B8F94" }} data-testid="restriction-readonly-hint">
              Les utilisateurs autorisés et leurs actions se règlent dans la fenêtre « Droits d'accès » de chaque utilisateur (Paramètres › Utilisateurs & droits d'accès › onglet Documents › « Types de documents autorisés »).
            </div>
          )}
          {!readOnlyUsers && <div className="mt-2" data-testid="restriction-add-inline">
            {!addOpen ? (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                disabled={nonAdmins.length === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[12px] hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: "#5A9EE0",
                  border: "1px dashed rgba(90,158,224,0.45)",
                }}
                data-testid="restriction-add-user-trigger"
                title={
                  nonAdmins.length === 0
                    ? "Aucun utilisateur non-admin dans le système — créez d'abord un compte Manager ou Utilisateur."
                    : available.length === 0
                      ? "Tous les utilisateurs non-admin sont déjà listés."
                      : "Ajouter un utilisateur autorisé à la restriction"
                }
              >
                <Plus size={12} /> Ajouter un utilisateur autorisé
              </button>
            ) : (
              <div
                className="rounded-md overflow-hidden"
                style={{ background: "#141414", border: "1px solid #2A2A2A" }}
              >
                <div
                  className="flex items-center gap-2 px-2.5 py-2"
                  style={{ borderBottom: "1px solid #2A2A2A", background: "#1A1A1A" }}
                >
                  <Search size={12} style={{ color: "#6B7280" }} />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Rechercher un utilisateur…"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    className="flex-1 h-6 bg-transparent focus:outline-none text-[12px]"
                    style={{ color: "#EDEDED" }}
                    data-testid="restriction-add-search"
                  />
                  <button
                    type="button"
                    onClick={() => { setAddOpen(false); setAddSearch(""); }}
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded hover:bg-white/10 flex items-center gap-0.5"
                    style={{ color: "#9CA3AF" }}
                    data-testid="restriction-add-close"
                  >
                    Fermer <ChevronUp size={11} />
                  </button>
                </div>
                <div className="max-h-[220px] overflow-y-auto py-1">
                  {filteredAvailable.length === 0 ? (
                    <div className="px-3 py-3 text-[11.5px] italic text-center leading-relaxed" style={{ color: "#8B8F94" }}>
                      {addSearch.trim()
                        ? "Aucun résultat pour cette recherche."
                        : nonAdmins.length === 0
                          ? <>
                              Aucun utilisateur <strong>non-admin</strong> dans le système.<br />
                              Les admins ont déjà accès automatiquement.<br />
                              <span style={{ color: "#5A9EE0" }}>Créez d'abord un compte Manager ou Utilisateur dans Paramètres → Utilisateurs.</span>
                            </>
                          : "Tous les utilisateurs non-admin sont déjà listés."}
                    </div>
                  ) : filteredAvailable.map((u) => {
                    const nm = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => addUser(u.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-white/5 transition"
                        data-testid={`restriction-add-user-${u.id}`}
                      >
                        <AvatarBlock user={u} size={22} />
                        <span style={{ color: "#EDEDED" }}>{nm}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={save}
            disabled={loading || saving}
            className="text-white border-0"
            style={{ background: "#2B579A" }}
            data-testid="restriction-save"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
